import * as React from 'react';
import { Slot } from 'radix-ui';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Every button, icon-only ones included (`size="icon…"`); `asChild` turns a
 * link or a menu trigger into one. A rounded rectangle (sm 36 · md 40 · lg 44)
 * that lifts 1px on hover and sinks 1px on press. `primary` is the one
 * near-black 3D button a screen should have; `secondary` is the white 3D
 * button; `approve` / `revision` / `decline` are the pastel verdict buttons;
 * `round` is the circular icon button with the radial highlight; `raised` is
 * a white control lifted off a card; `ghost` is words (or an icon) with a
 * soft hover; `dock` is the dock tray's small round control (36px, the
 * dock's icon ink and hover); `back` is the small white "← Home" pill
 * (`BackButton`). `pill` makes it fully round, `full` fills its
 * row (with `between`, words left and icon right). `size="chip"` is the 28px round
 * chip a row carries (a document, a review waiting). `size="tab"` is a tab that is a
 * link or a button, at the one tab size (30px, 13px words: `Segmented size="sm"`'s).
 */
export const buttonVariants = cva('dw-btn [&_svg]:pointer-events-none', {
  variants: {
    variant: {
      primary: 'dw-btn-primary',
      secondary: 'dw-btn-secondary',
      approve: 'dw-btn-approve',
      revision: 'dw-btn-revision',
      decline: 'dw-btn-danger',
      round: 'dw-btn-round',
      raised: 'dw-btn-raised',
      ghost: 'dw-btn-ghost',
      link: 'dw-btn-link',
      dock: 'dw-btn-dock',
      back: 'dw-btn-secondary dw-btn-sm dw-btn-pill dw-back',
    },
    size: {
      sm: 'dw-btn-sm',
      md: '',
      lg: 'dw-btn-lg',
      icon: 'dw-btn-icon',
      'icon-sm': 'dw-btn-icon dw-btn-sm',
      'icon-lg': 'dw-btn-icon dw-btn-lg',
      'icon-xs': 'dw-btn-icon h-6 w-6 rounded-full [&_svg]:size-3.5',
      send: 'dw-btn-send',
      chip: 'dw-btn-chip',
      tab: 'dw-btn-tab',
    },
    pill: { true: 'dw-btn-pill' },
    full: { true: 'dw-btn-full' },
    between: { true: 'dw-btn-between' },
  },
  defaultVariants: { variant: 'secondary', size: 'md' },
});

export interface ButtonProps extends React.ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export function Button({ className, variant, size, pill, full, between, asChild = false, type, ...props }: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button';
  return (
    <Comp
      data-slot="button"
      type={asChild ? undefined : (type ?? 'button')}
      className={cn(buttonVariants({ variant, size, pill, full, between }), className)}
      {...props}
    />
  );
}
